import React from 'react';
import {
  Stack,
  Text,
  Dropdown,
  IDropdownOption,
  TextField,
  Toggle,
  Separator,
  Label,
  MessageBar,
  MessageBarType
} from '@fluentui/react';
import {
  CalendarRegular,
  ClockRegular,
  ArrowRepeatAllRegular,
  ErrorCircleRegular
} from '@fluentui/react-icons';

interface ScheduleConfiguration {
  enabled: boolean;
  frequency: 'once' | 'hourly' | 'daily' | 'weekly' | 'monthly' | 'custom';
  startTime?: string; // ISO datetime for first run
  timeOfDay?: string; // HH:MM format
  dayOfWeek?: number; // 0-6 for weekly
  dayOfMonth?: number; // 1-31 for monthly
  intervalMinutes?: number; // For hourly
  cronExpression?: string; // For custom
  timezone: string;
  endDate?: string; // Optional end date
  maxRuns?: number; // Maximum executions
  retryOnFailure: boolean;
  retryCount: number;
  retryDelayMinutes: number;
}

interface ScheduleConfigTabProps {
  schedule: ScheduleConfiguration;
  onChange: (schedule: ScheduleConfiguration) => void;
}

const ScheduleConfigTab: React.FC<ScheduleConfigTabProps> = ({
  schedule,
  onChange
}) => {
  const frequencyOptions: IDropdownOption[] = [
    { key: 'once', text: 'Run Once' },
    { key: 'hourly', text: 'Every Hour' },
    { key: 'daily', text: 'Daily' },
    { key: 'weekly', text: 'Weekly' },
    { key: 'monthly', text: 'Monthly' },
    { key: 'custom', text: 'Custom (Cron)' }
  ];

  const timezoneOptions: IDropdownOption[] = [
    { key: 'Asia/Jerusalem', text: 'Jerusalem (UTC+2/+3)' },
    { key: 'UTC', text: 'UTC' },
    { key: 'America/New_York', text: 'Eastern Time (UTC-5/-4)' },
    { key: 'America/Chicago', text: 'Central Time (UTC-6/-5)' },
    { key: 'America/Denver', text: 'Mountain Time (UTC-7/-6)' },
    { key: 'America/Los_Angeles', text: 'Pacific Time (UTC-8/-7)' },
    { key: 'Europe/London', text: 'London (UTC+0/+1)' },
    { key: 'Europe/Paris', text: 'Paris (UTC+1/+2)' },
    { key: 'Asia/Tokyo', text: 'Tokyo (UTC+9)' },
    { key: 'Australia/Sydney', text: 'Sydney (UTC+10/+11)' }
  ];

  const dayOfWeekOptions: IDropdownOption[] = [
    { key: 0, text: 'Sunday' },
    { key: 1, text: 'Monday' },
    { key: 2, text: 'Tuesday' },
    { key: 3, text: 'Wednesday' },
    { key: 4, text: 'Thursday' },
    { key: 5, text: 'Friday' },
    { key: 6, text: 'Saturday' }
  ];

  const handleScheduleChange = (field: keyof ScheduleConfiguration, value: any) => {
    const updatedSchedule = { ...schedule, [field]: value };
    
    // Reset frequency-specific fields when frequency changes
    if (field === 'frequency') {
      updatedSchedule.dayOfWeek = undefined;
      updatedSchedule.dayOfMonth = undefined;
      updatedSchedule.intervalMinutes = undefined;
      updatedSchedule.cronExpression = undefined;
      
      // Set defaults for specific frequencies
      if (value === 'hourly') {
        updatedSchedule.intervalMinutes = 60;
      } else if (value === 'daily') {
        updatedSchedule.timeOfDay = '07:00';
      } else if (value === 'weekly') {
        updatedSchedule.dayOfWeek = 1; // Monday
        updatedSchedule.timeOfDay = '07:00';
      } else if (value === 'monthly') {
        updatedSchedule.dayOfMonth = 1;
        updatedSchedule.timeOfDay = '07:00';
      }
    }
    
    onChange(updatedSchedule);
  };

  const getCronExample = () => {
    switch (schedule.frequency) {
      case 'hourly':
        return '0 * * * * (every hour)';
      case 'daily':
        return '0 7 * * * (daily at 7:00 AM)';
      case 'weekly':
        return '0 7 * * 1 (Mondays at 7:00 AM)';
      case 'monthly':
        return '0 7 1 * * (1st of month at 7:00 AM)';
      default:
        return '0 7 * * * (daily at 7:00 AM)';
    }
  };

  return (
    <Stack tokens={{ childrenGap: 16 }}>
      <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center">
        <CalendarRegular style={{ fontSize: 20, color: '#0078d4' }} />
        <Text variant="large" style={{ fontWeight: 600 }}>
          Schedule Configuration
        </Text>
      </Stack>

      <Text variant="medium" style={{ color: '#666' }}>
        Configure when and how often this connector should run. You can run it once or set up recurring execution.
      </Text>

      <Separator />

      {/* Enable Scheduling Toggle */}
      <Stack>
        <Toggle
          label="Enable Scheduling"
          checked={schedule.enabled}
          onChange={(_, checked) => handleScheduleChange('enabled', checked || false)}
          onText="Scheduled"
          offText="Manual"
        />
        {!schedule.enabled && (
          <Text variant="small" style={{ color: '#666', marginTop: 4 }}>
            Job will only run when manually triggered
          </Text>
        )}
      </Stack>

      {schedule.enabled && (
        <>
          <Separator />

          {/* Frequency Selection */}
          <Dropdown
            label="Frequency"
            options={frequencyOptions}
            selectedKey={schedule.frequency}
            onChange={(_, option) => handleScheduleChange('frequency', option?.key)}
            placeholder="Select frequency"
            required
          />

          {/* Frequency-specific Configuration */}
          {schedule.frequency === 'hourly' && (
            <TextField
              label="Interval (minutes)"
              type="number"
              value={schedule.intervalMinutes?.toString() || '60'}
              onChange={(_, value) => handleScheduleChange('intervalMinutes', parseInt(value || '60'))}
              suffix="minutes"
              min={1}
              max={1440}
            />
          )}

          {(schedule.frequency === 'daily' || schedule.frequency === 'weekly' || schedule.frequency === 'monthly') && (
            <TextField
              label="Time of Day"
              type="time"
              value={schedule.timeOfDay || '07:00'}
              onChange={(_, value) => handleScheduleChange('timeOfDay', value)}
              placeholder="HH:MM"
            />
          )}

          {schedule.frequency === 'weekly' && (
            <Dropdown
              label="Day of Week"
              options={dayOfWeekOptions}
              selectedKey={schedule.dayOfWeek}
              onChange={(_, option) => handleScheduleChange('dayOfWeek', option?.key)}
              placeholder="Select day"
            />
          )}

          {schedule.frequency === 'monthly' && (
            <TextField
              label="Day of Month"
              type="number"
              value={schedule.dayOfMonth?.toString() || '1'}
              onChange={(_, value) => handleScheduleChange('dayOfMonth', parseInt(value || '1'))}
              min={1}
              max={31}
              suffix="(1-31)"
            />
          )}

          {schedule.frequency === 'custom' && (
            <Stack tokens={{ childrenGap: 8 }}>
              <TextField
                label="Cron Expression"
                value={schedule.cronExpression || ''}
                onChange={(_, value) => handleScheduleChange('cronExpression', value || '')}
                placeholder="0 7 * * * (daily at 7:00 AM)"
                description="Use standard cron format: second minute hour day month dayofweek"
              />
              <MessageBar messageBarType={MessageBarType.info}>
                <Text variant="small">
                  <strong>Examples:</strong><br />
                  • <code>0 */30 * * * *</code> - Every 30 minutes<br />
                  • <code>0 0 9 * * 1-5</code> - 9:00 AM on weekdays<br />
                  • <code>0 0 0 1 * *</code> - First day of every month
                </Text>
              </MessageBar>
            </Stack>
          )}

          <Separator />

          {/* Timezone Selection */}
          <Dropdown
            label="Timezone"
            options={timezoneOptions}
            selectedKey={schedule.timezone}
            onChange={(_, option) => handleScheduleChange('timezone', option?.key)}
            placeholder="Select timezone"
            required
          />

          <Separator />

          {/* Optional Settings */}
          <Stack tokens={{ childrenGap: 12 }}>
            <Text variant="mediumPlus" style={{ fontWeight: 600 }}>
              Optional Settings
            </Text>

            <TextField
              label="Start Time (Optional)"
              type="datetime-local"
              value={schedule.startTime || ''}
              onChange={(_, value) => handleScheduleChange('startTime', value)}
              description="When should the first execution occur? Leave empty to start immediately"
            />

            <TextField
              label="End Date (Optional)"
              type="date"
              value={schedule.endDate || ''}
              onChange={(_, value) => handleScheduleChange('endDate', value)}
              description="Stop scheduling after this date"
            />

            <TextField
              label="Maximum Runs (Optional)"
              type="number"
              value={schedule.maxRuns?.toString() || ''}
              onChange={(_, value) => handleScheduleChange('maxRuns', value ? parseInt(value) : undefined)}
              min={1}
              description="Limit total number of executions"
            />
          </Stack>

          <Separator />

          {/* Retry Configuration */}
          <Stack tokens={{ childrenGap: 12 }}>
            <Text variant="mediumPlus" style={{ fontWeight: 600 }}>
              Retry Configuration
            </Text>

            <Toggle
              label="Retry on Failure"
              checked={schedule.retryOnFailure}
              onChange={(_, checked) => handleScheduleChange('retryOnFailure', checked || false)}
              onText="Enabled"
              offText="Disabled"
            />

            {schedule.retryOnFailure && (
              <Stack horizontal tokens={{ childrenGap: 12 }}>
                <TextField
                  label="Retry Count"
                  type="number"
                  value={schedule.retryCount?.toString() || '3'}
                  onChange={(_, value) => handleScheduleChange('retryCount', parseInt(value || '3'))}
                  min={1}
                  max={10}
                  style={{ flex: 1 }}
                />
                <TextField
                  label="Retry Delay (minutes)"
                  type="number"
                  value={schedule.retryDelayMinutes?.toString() || '15'}
                  onChange={(_, value) => handleScheduleChange('retryDelayMinutes', parseInt(value || '15'))}
                  min={1}
                  max={1440}
                  style={{ flex: 1 }}
                />
              </Stack>
            )}
          </Stack>

          {/* Summary */}
          {schedule.frequency !== 'once' && (
            <>
              <Separator />
              <MessageBar messageBarType={MessageBarType.info}>
                <Stack tokens={{ childrenGap: 4 }}>
                  <Text variant="small" style={{ fontWeight: 600 }}>
                    Schedule Summary:
                  </Text>
                  <Text variant="small">
                    Frequency: {frequencyOptions.find(f => f.key === schedule.frequency)?.text}
                    {schedule.timeOfDay && ` at ${schedule.timeOfDay}`}
                    {schedule.dayOfWeek !== undefined && ` on ${dayOfWeekOptions.find(d => d.key === schedule.dayOfWeek)?.text}`}
                    {schedule.dayOfMonth && ` on day ${schedule.dayOfMonth}`}
                  </Text>
                  <Text variant="small">
                    Timezone: {timezoneOptions.find(tz => tz.key === schedule.timezone)?.text}
                  </Text>
                  {schedule.frequency === 'custom' && schedule.cronExpression && (
                    <Text variant="small">
                      Cron: <code>{schedule.cronExpression}</code>
                    </Text>
                  )}
                </Stack>
              </MessageBar>
            </>
          )}
        </>
      )}
    </Stack>
  );
};

export default ScheduleConfigTab;